/**
 * What a hover over a sidebar destination starts.
 *
 * Opening a page now costs two serial things — its chunk has to arrive before
 * its code can ask for data — and a pointer resting on a nav item is a reliable
 * few hundred milliseconds of notice. So a hover kicks off both: the route's
 * dynamic import (the same module specifier `console-routes.tsx` uses, so it is
 * the same chunk and React.lazy finds it already resolved) and whichever reads
 * the page opens with.
 *
 * The queries are the page's OWN declared options, not a hand-copied
 * equivalent. A prefetch under a different key or with different params is a
 * request that costs the same and warms nothing — which is exactly the bug the
 * sidebar's new-orders badge used to have. Their `staleTime` governs too, so a
 * pointer sweeping down the nav does not turn into a burst of duplicate reads.
 *
 * Four destinations warm only their chunk. Dashboard, Users, Companies and
 * Orders build their requests inside the route module instead of declaring them
 * in `lib/queries.ts`, so there is nothing here that could warm the same key —
 * and guessing at it would just add a second, differently-keyed copy.
 */
import type { QueryClient } from "@tanstack/react-query";

import {
  approvalsQuery,
  dataIntegrityQuery,
  docusealHealthQuery,
  documentsListQuery,
  einCorrectionsListQuery,
  faxListQuery,
  federalTaxDeadlinesQuery,
  federalTaxStatsQuery,
  federalTaxTriageQuery,
  FEDERAL_TAX_TRIAGE_TABS,
  feedConnectionsQuery,
  formationListQuery,
  ledgerBooksQuery,
  mailroomListQuery,
  notificationCatalogueQuery,
  partnersOverviewQuery,
  notificationsListQuery,
  notificationRegistryStatsQuery,
  opsAlertsListQuery,
  paymentsListQuery,
  registeredAgentListQuery,
  stripeListQuery,
  stripeOverviewQuery,
  INBOX_LANDING,
  supportInboxCountsQuery,
  supportInboxListQuery,
  trustpilotInvitationsQuery,
  webhooksListQuery,
  WORK_QUEUE_LANDING,
  workQueueListQuery,
} from "@/lib/queries";
import { aiSettingsQuery } from "@/lib/ai-settings";
import { mailListQuery } from "@/lib/mail";
import {
  emailBrandQuery,
  emailBrandVersionsQuery,
  emailContactsQuery,
  emailSegmentsQuery,
} from "@/lib/queries-email";
import {
  emailCampaignsQuery,
  emailTemplatesQuery,
} from "@/lib/queries-email-marketing";
import { stateFilingsListQuery } from "@/lib/state-filings";

/** Query options as the builders return them — enough for `prefetchQuery`. */
type PrefetchOptions = Parameters<QueryClient["prefetchQuery"]>[0];

interface NavPrefetch {
  /** The route's chunk, by the specifier `console-routes.tsx` splits on. */
  chunk: () => Promise<unknown>;
  /** The reads the page opens with, at their default (unfiltered) params. */
  queries?: () => PrefetchOptions[];
}

/** Defaults matching a nav click: page one, no search, no filters. */
const LIST_PAGE = 1;
const NO_FILTER = "";

export const NAV_PREFETCH: Record<string, NavPrefetch> = {
  "/": {
    chunk: () => import("@/routes/WorkQueue"),
    queries: () => [workQueueListQuery(WORK_QUEUE_LANDING)],
  },
  "/dashboard": { chunk: () => import("@/routes/Dashboard") },
  "/users": { chunk: () => import("@/routes/Users") },
  "/companies": { chunk: () => import("@/routes/Companies") },
  "/orders": { chunk: () => import("@/routes/Orders") },
  "/formation": {
    chunk: () => import("@/routes/Formation"),
    queries: () => [
      formationListQuery({
        page: LIST_PAGE,
        stageGroup: "open",
        formationState: NO_FILTER,
        companyId: NO_FILTER,
        search: NO_FILTER,
        paymentScope: "paid",
      }),
    ],
  },
  "/registered-agent": {
    chunk: () => import("@/routes/registered-agent/RegisteredAgent"),
    queries: () => [registeredAgentListQuery({ page: LIST_PAGE })],
  },
  "/federal-tax": {
    chunk: () => import("@/routes/FederalTax"),
    // The landing view is triage, so that is what a hover warms — the plain
    // queue is behind `?view=all` and warming it would be a request for a list
    // the click is not going to show.
    queries: () => [
      federalTaxStatsQuery(),
      federalTaxDeadlinesQuery(),
      federalTaxTriageQuery({
        page: LIST_PAGE,
        tab: FEDERAL_TAX_TRIAGE_TABS[0]!.key,
        search: NO_FILTER,
      }),
    ],
  },
  "/state-filings": {
    chunk: () => import("@/routes/StateFilings"),
    queries: () => [
      stateFilingsListQuery({
        page: LIST_PAGE,
        status: NO_FILTER,
        state: NO_FILTER,
        needs: NO_FILTER,
        companyId: NO_FILTER,
        taxYear: NO_FILTER,
      }),
    ],
  },
  "/documents": {
    chunk: () => import("@/routes/Documents"),
    queries: () => [
      documentsListQuery({
        page: LIST_PAGE,
        search: NO_FILTER,
        category: NO_FILTER,
        sort: "newest",
        companyId: NO_FILTER,
      }),
    ],
  },
  "/mailroom": {
    chunk: () => import("@/routes/Mailroom"),
    queries: () => [
      mailroomListQuery({
        page: LIST_PAGE,
        search: NO_FILTER,
        companyId: NO_FILTER,
        category: NO_FILTER,
      }),
    ],
  },
  "/ein-corrections": {
    chunk: () => import("@/routes/EinCorrections"),
    queries: () => [
      einCorrectionsListQuery({ page: LIST_PAGE, status: NO_FILTER }),
    ],
  },
  "/fax": {
    chunk: () => import("@/routes/Fax"),
    queries: () => [
      faxListQuery({
        page: LIST_PAGE,
        direction: NO_FILTER,
        status: NO_FILTER,
        documentType: NO_FILTER,
        companyId: NO_FILTER,
        search: NO_FILTER,
      }),
    ],
  },
  "/mail": {
    chunk: () => import("@/routes/Mail"),
    queries: () => [
      mailListQuery({
        page: LIST_PAGE,
        status: NO_FILTER,
        purpose: NO_FILTER,
        companyId: NO_FILTER,
        search: NO_FILTER,
      }),
    ],
  },
  "/docuseal": {
    chunk: () => import("@/routes/DocusealHealth"),
    queries: () => [docusealHealthQuery()],
  },
  // Opens on Overview: the totals and "Needs attention"'s first lists.
  "/billing": {
    chunk: () => import("@/routes/Billing"),
    queries: () => [
      stripeOverviewQuery(),
      stripeListQuery("subscriptions", LIST_PAGE),
      stripeListQuery("payment-intents", LIST_PAGE),
    ],
  },
  "/payments": {
    chunk: () => import("@/routes/payments/Payments"),
    queries: () => [
      paymentsListQuery({
        page: LIST_PAGE,
        method: NO_FILTER,
        status: NO_FILTER,
        search: NO_FILTER,
      }),
    ],
  },
  "/webhooks": {
    chunk: () => import("@/routes/Webhooks"),
    queries: () => [
      webhooksListQuery({
        page: LIST_PAGE,
        source: NO_FILTER,
        processingStatus: NO_FILTER,
        eventType: NO_FILTER,
        search: NO_FILTER,
        window: NO_FILTER,
      }),
    ],
  },
  "/alerts": {
    chunk: () => import("@/routes/Alerts"),
    queries: () => [opsAlertsListQuery({ page: LIST_PAGE, reason: NO_FILTER })],
  },
  // The pending-deletion queue (plan 05), opened on its default pending view.
  "/approvals": {
    chunk: () => import("@/routes/approvals/Approvals"),
    queries: () => [
      approvalsQuery({ status: "pending", page: LIST_PAGE, pageSize: 25 }),
    ],
  },
  // The ledger lands on a company picker: the review queue is per book and
  // waits for one to be picked, so the landing's reads are the books list
  // (the picker and its counts) and the connections count.
  "/accounting": {
    chunk: () => import("@/routes/accounting/Accounting"),
    queries: () => [ledgerBooksQuery(), feedConnectionsQuery()],
  },
  // Emails (owner 2026-10-16): the sidebar lands on Campaigns; each Emails
  // tab warms its own page on hover.
  "/emails": {
    chunk: () => import("@/routes/emails/campaigns/Campaigns"),
    queries: () => [emailCampaignsQuery({ status: NO_FILTER, q: NO_FILTER })],
  },
  "/emails/templates": {
    chunk: () => import("@/routes/emails/Templates"),
    queries: () => [emailTemplatesQuery("all")],
  },
  "/emails/contacts": {
    chunk: () => import("@/routes/emails/Contacts"),
    queries: () => [
      emailContactsQuery({
        kind: NO_FILTER,
        status: NO_FILTER,
        q: NO_FILTER,
        page: LIST_PAGE,
      }),
    ],
  },
  "/emails/segments": {
    chunk: () => import("@/routes/emails/Segments"),
    queries: () => [emailSegmentsQuery("active")],
  },
  "/emails/brand": {
    chunk: () => import("@/routes/emails/Brand"),
    queries: () => [emailBrandQuery(), emailBrandVersionsQuery()],
  },
  // Transactional (the old Notifications) opens on Overview: the catalogue's
  // totals, the latest failed sends and the per-template breakdown. Warming
  // the delivery log instead would pay for a read the landing view does not
  // make — the same differently-keyed miss this table exists to stop.
  "/emails/transactional": {
    chunk: () => import("@/routes/Notifications"),
    queries: () => [
      notificationCatalogueQuery(),
      notificationRegistryStatsQuery(),
      notificationsListQuery({
        page: LIST_PAGE,
        status: "failed",
        notificationType: NO_FILTER,
        recipientAuthUserId: NO_FILTER,
      }),
    ],
  },
  "/operations": {
    chunk: () => import("@/routes/Operations"),
    // Only the first of the screen's five monitors. Warming all five on a
    // hover would spend an R2 bucket walk and four anti-join count scans on a
    // pointer that may never click.
    queries: () => [dataIntegrityQuery()],
  },
  "/emails/trustpilot": {
    chunk: () => import("@/routes/Trustpilot"),
    queries: () => [
      trustpilotInvitationsQuery({
        page: LIST_PAGE,
        status: NO_FILTER,
        search: NO_FILTER,
      }),
    ],
  },
  // Opens on the Overview: the counts and the draft batch that say what needs a decision.
  "/partners": {
    chunk: () => import("@/routes/partners/Overview"),
    queries: () => [partnersOverviewQuery()],
  },
  // Opens on the Inbox: open tickets, anyone's, and the tab counts.
  "/support": {
    chunk: () => import("@/routes/support/Support"),
    queries: () => [
      supportInboxListQuery(INBOX_LANDING),
      supportInboxCountsQuery(),
    ],
  },
  // The operations table only: Spend's ledger summary waits for the click.
  "/settings/ai": {
    chunk: () => import("@/routes/settings/AiSettings"),
    queries: () => [aiSettingsQuery()],
  },
};

/**
 * Start the work behind one destination. Fire and forget by design: a failed
 * prefetch must be silent — the page will ask again for itself and render its
 * own error state if it has to.
 */
export function prefetchNav(client: QueryClient, to: string): void {
  const entry = NAV_PREFETCH[to];
  if (!entry) return;

  entry.chunk().catch(() => {});
  for (const options of entry.queries?.() ?? []) {
    client.prefetchQuery(options).catch(() => {});
  }
}
